import React, { createContext, useContext, useState, type ReactNode } from "react";
import type { Document } from "@/services/api/documents/types";
import type { IngestField } from "@/services/api/data-hub/types";

// Types for the upload context
export interface UploadOptions {
  // MIME types to accept (e.g. ['image/jpeg', 'image/png', 'application/pdf'])
  acceptedMimeTypes?: string[];
  // Maximum file size in bytes
  maxSize?: number;
  // Minimum file size in bytes
  minSize?: number;
  // Whether to allow multiple files
  multiple?: boolean;
}

export interface UploadCallbacks<T = Document> {
  // Called when upload is successful with the response data
  onSuccess?: (data: T) => void;
  // Called when upload fails with the error
  onError?: (error: Error) => void;
  // Called when upload starts
  onStart?: () => void;
  // Called when upload progress changes
  onProgress?: (progress: number) => void;
  // Called before upload to validate the file
  onValidate?: (file: File) => boolean | Promise<boolean>;
}

export interface FileUploadContextType {
  // State
  uploading: boolean;
  progress: number;
  error: Error | null;

  // Methods
  validateFile: (file: File, options?: UploadOptions) => boolean;
  uploadFile: <T = Document | IngestField >(
    file: File,
    uploadFn: (file: File) => Promise<T>,
    options?: UploadOptions,
    callbacks?: UploadCallbacks<T>
  ) => Promise<T | null>;
  uploadFiles: <T = Document>(
    files: File[],
    uploadFn: (file: File) => Promise<T>,
    options?: UploadOptions,
    callbacks?: UploadCallbacks<T>
  ) => Promise<(T | null)[]>;
  reset: () => void;
}

const FileUploadContext = createContext<FileUploadContextType | undefined>(
  undefined
);

export interface FileUploadProviderProps {
  children: ReactNode;
}

export const FileUploadProvider: React.FC<FileUploadProviderProps> = ({
  children,
}) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<Error | null>(null);

  const reset = () => {
    setUploading(false);
    setProgress(0);
    setError(null);
  };

  const validateFile = (file: File, options?: UploadOptions): boolean => {
    if (!file) {
      setError(new Error("No file provided"));
      return false;
    }

    // Check MIME type if specified
    if (options?.acceptedMimeTypes && options.acceptedMimeTypes.length > 0) {
      if (!options.acceptedMimeTypes.includes(file.type)) {
        setError(
          new Error(
            `File type not accepted. Accepted types: ${options.acceptedMimeTypes.join(
              ", "
            )}`
          )
        );
        return false;
      }
    }

    // Check file size if specified
    if (options?.maxSize && file.size > options.maxSize) {
      setError(
        new Error(
          `File size exceeds maximum allowed size of ${
            options.maxSize / 1024 / 1024
          }MB`
        )
      );
      return false;
    }

    if (options?.minSize && file.size < options.minSize) {
      setError(
        new Error(
          `File size is less than minimum required size of ${
            options.minSize / 1024
          }KB`
        )
      );
      return false;
    }

    return true;
  };

  const uploadFile = async <T = Document | IngestField,>(
    file: File,
    uploadFn: (file: File) => Promise<T>,
    options?: UploadOptions,
    callbacks?: UploadCallbacks<T>
  ): Promise<T | null> => {
    try {
      reset();

      // Validate file
      if (!validateFile(file, options)) {
        if (callbacks?.onError) callbacks.onError(error!);
        return null;
      }

      // Run custom validation if provided
      if (callbacks?.onValidate) {
        const isValid = await callbacks.onValidate(file);
        if (!isValid) {
          setError(new Error("File validation failed"));
          if (callbacks?.onError)
            callbacks.onError(new Error("File validation failed"));
          return null;
        }
      }

      // Start upload
      setUploading(true);
      if (callbacks?.onStart) callbacks.onStart();

      // We don't have progress tracking in the uploadFn, so we just set it to indeterminate
      setProgress(10);
      if (callbacks?.onProgress) callbacks.onProgress(10);

      // Upload the file
      const result = await uploadFn(file);

      // Set progress to complete
      setProgress(100);
      if (callbacks?.onProgress) callbacks.onProgress(100);

      // Handle success
      if (callbacks?.onSuccess) callbacks.onSuccess(result);

      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      if (callbacks?.onError) callbacks.onError(error);
      return null;
    } finally {
      setUploading(false);
    }
  };

  const uploadFiles = async <T = Document,>(
    files: File[],
    uploadFn: (file: File) => Promise<T>,
    options?: UploadOptions,
    callbacks?: UploadCallbacks<T>
  ): Promise<(T | null)[]> => {
    if (!files.length) {
      const error = new Error("No files provided");
      setError(error);
      if (callbacks?.onError) callbacks.onError(error);
      return [];
    }

    // Check if multiple files are allowed
    if (files.length > 1 && options?.multiple === false) {
      const error = new Error("Multiple files are not allowed");
      setError(error);
      if (callbacks?.onError) callbacks.onError(error);
      return [];
    }

    try {
      reset();
      setUploading(true);
      if (callbacks?.onStart) callbacks.onStart();

      const results: (T | null)[] = [];
      let completedCount = 0;

      for (const file of files) {
        try {
          // Validate each file
          if (!validateFile(file, options)) {
            results.push(null);
            continue;
          }

          // Run custom validation if provided
          if (callbacks?.onValidate) {
            const isValid = await callbacks.onValidate(file);
            if (!isValid) {
              results.push(null);
              continue;
            }
          }

          // Upload the file
          const result = await uploadFn(file);
          results.push(result);

          // Update progress
          completedCount++;
          const progressPercent = Math.round(
            (completedCount / files.length) * 100
          );
          setProgress(progressPercent);
          if (callbacks?.onProgress) callbacks.onProgress(progressPercent);
        } catch {
          results.push(null);
        }
      }

      // Call onSuccess with all successful results
      const successfulResults = results.filter((r): r is T => r !== null);
      if (successfulResults.length > 0 && callbacks?.onSuccess) {
        callbacks.onSuccess(successfulResults as any);
      }

      return results;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      if (callbacks?.onError) callbacks.onError(error);
      return [];
    } finally {
      setUploading(false);
    }
  };

  const value: FileUploadContextType = {
    uploading,
    progress,
    error,
    validateFile,
    uploadFile,
    uploadFiles,
    reset,
  };

  return (
    <FileUploadContext.Provider value={value}>
      {children}
    </FileUploadContext.Provider>
  );
};

export const useFileUpload = (): FileUploadContextType => {
  const context = useContext(FileUploadContext);
  if (!context) {
    throw new Error("useFileUpload must be used within a FileUploadProvider");
  }
  return context;
};

// Helper hooks for common document upload scenarios
export function useDocumentUpload<T = Document>(
  uploadFn: (file: File) => Promise<T>,
  options?: UploadOptions,
  callbacks?: UploadCallbacks<T>
) {
  const { uploadFile, uploading, progress, error, reset } = useFileUpload();

  const defaultOptions: UploadOptions = {
    acceptedMimeTypes: [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-powerpoint",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      "text/plain",
      "text/csv",
      "application/json",
      "application/xml",
      "text/xml",
      "image/jpeg",
      "image/png",
      "image/gif",
    ],
    maxSize: 100 * 1024 * 1024, // 100MB
    multiple: false,
    ...options,
  };

  const upload = async (file: File): Promise<T | null> => {
    return uploadFile(file, uploadFn, defaultOptions, callbacks);
  };

  return {
    upload,
    uploading,
    progress,
    error,
    reset,
  };
}
